import { Col, Modal, Row } from 'antd';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  backgroundColorParsing, getLangCookie,
} from 'Utils/UtilityFunctions';
import {
  extendedSearchLoader, primaryColor, primaryHeadingColor, secondaryFont, wMedium,
} from 'Utils/utilsStyle';
import { useNavigate } from 'react-router-dom';
import { COMPANY_OVERVIEW_ROUTE, PERSONALITY_INSIGHTS_ROUTE } from 'Utils/Constants';
import { Executive } from 'Types/PersonTypes';
import { SearchResultsRequest } from 'Types/GlobalSearchResultsTypes';
import { selectScreenName } from 'Selector/GlobalSearchContentSelector';
import { selectLoading, selectPersonLoaded, selectPersonLoading } from 'Selector/GlobalSearchResultsSelector';
import { Company } from 'Types/CompaniesTypes';
import NoDataFound from '../components/NoDataFound';
import PeopleCardLarge from '../components/PeopleCardLarge';
import SearchPersonSkeletonBlock from '../skeleton/SearchPersonSkeletonBlock';
import { actions as searchActions } from '../../../Slice/GlobalSearchResultsSlice';
import CompanyCardLarge from '../components/CompanyCardLarge';
import CompanySkeleton from '../skeleton/CompanySkeleton';

const LoaderWrapper = styled.div`
  .main{
    background:white;
    border:1px solid ${primaryColor};
    padding: 60px 30px;
    text-align: center;
    border-radius:16px;
    margin:0 18px;
    .loader-style{
      height:90px;
      width:90px;
    }
    .text{
      font-size:20px;
      color:${primaryHeadingColor};
      font-weight: ${wMedium};
      font-family: ${secondaryFont};
      letter-spacing: -0.45px;
      line-height: 20px;
      margin-top: 20px;
      margin-bottom: 5px;
      width:160px;
    }
  }
`;

const NoDataWrapper = styled.div`
  width: 100%;
  .no_data_found {
    height: calc(-542px + 100vh) !important;
  }
`;

type Props = {
  type: 'person' | 'company';
  count: number;
  searchQuery: string;
  isVisible: boolean
  setIsVisible: (value: boolean) => void
  persons?: Executive[];
  companies?: Company[];
} & typeof defaultProps;

const defaultProps = {
  persons: [] as Executive[],
  companies: [] as Company[],
};

const SearchResultPopup = function SearchResultPopup(props: Props) {
  const {
    type, count, searchQuery, persons, companies, isVisible, setIsVisible,
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [page, setPage] = useState<number>(2);
  const searchLoading = useSelector(selectLoading);
  const linkedinPeopleLoading = useSelector(selectPersonLoading);
  const personLoaded = useSelector(selectPersonLoaded);
  const screenName = useSelector(selectScreenName);
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const isLoading = searchLoading;
  const linkedinLoading = linkedinPeopleLoading;
  const isPerson = type === 'person';

  const fetchMore = () => {
    const obj: SearchResultsRequest = {
      query: isPerson ? `person:${searchQuery}` : `company:${searchQuery}`,
      screenName,
      page,
      defaultLanguage: getLangCookie(),
    };
    setPage(page + 1);
    dispatch(searchActions.globalSearchResults(obj));
  };

  const handleClick = (index: number, id: number, personType?: string) => {
    setIsVisible(false);
    setActiveIndex(index);
    if (isPerson) {
      navigate(`${PERSONALITY_INSIGHTS_ROUTE}${id}&personType=${personType || 'EXECUTIVE'}`, { replace: true, state: { from: 'salesforce-person-search' } });
    } else {
      navigate(`${COMPANY_OVERVIEW_ROUTE}${id}`, { replace: true });
    }
  };

  const getPersonInfiniteScroll = () => (
    <InfiniteScroll
      hasMore
      dataLength={persons.length}
      next={fetchMore}
      className="o-x-hidden infinite-scroll-div mr-1 person-comparison-row"
      loader={false}
      height="auto"
    >
      <Row gutter={[16, 16]} className="comparison-grid">
        {persons.length ? persons.map((person, key) => (
          <Col md={{ span: 6 }} className="person-comparison-col">
            <div
              role="button"
              tabIndex={0}
              className={classNames('card', { activeCard: key === activeIndex })}
              onClick={() => handleClick(key, person.personId, person.personType || '')}
              onKeyPress={() => handleClick(key, person.personId, person.personType || '')}
            >
              <PeopleCardLarge
                diameter="130px"
                diameterXxsm="125px"
                imageDiameter="130px"
                holoDiameterWidth="125px"
                holoDiameterHeight="115px"
                holoDiameterXxsmHeight="110px"
                holoDiameterXxsmWidth="120px"
                key={person?.personId}
                persona={person?.archeType?.color}
                logo={person?.personImage}
                personName={person?.personName}
                tag={person?.personDesignation}
                personaType={person?.archeType?.name}
                wrapperColor={backgroundColorParsing(person?.personalityTypes)}
                personalityTypesColors={person?.personalityTypes}
                hidePersonality={false}
                personId={person?.personId}
                company={person?.companyName}
                follow={!person?.personAdded}
                disableNavigation
                personAddedFlag={person.personAdded}
              />
            </div>
          </Col>
        )) : null}
        {isLoading && (
          <div className="person-comparison-skeleton-col profile-confirmation">
            <SearchPersonSkeletonBlock />
          </div>
        )}
        {(linkedinLoading || !personLoaded) && (
          <Col md={{ span: 6 }} className="person-comparison-col outer-search">
            <LoaderWrapper>
              <div className="main">
                <img className="loader-style" src={extendedSearchLoader} alt="extended-loader" />
                <p className="text">
                  {
                    'Uncovering more People using xiQ\'s extended Search.'
                  }

                </p>
              </div>
            </LoaderWrapper>
          </Col>
        )}
        {!isLoading && !linkedinLoading && personLoaded && !persons?.length ? <NoDataWrapper><NoDataFound title="No Record Found" /></NoDataWrapper> : null}
      </Row>
    </InfiniteScroll>
  );

  const getCompanyInfiniteScroll = () => (
    <InfiniteScroll
      hasMore
      dataLength={companies.length}
      next={fetchMore}
      className="o-x-hidden infinite-scroll-div mr-1 person-comparison-row"
      loader={false}
      height="auto"
    >
      <Row gutter={[16, 16]} className="comparison-grid">
        {companies.length ? companies.map((company, key) => (
          <Col
            xxl={{ span: 8 }}
            xl={{ span: 8 }}
            lg={{ span: 6 }}
            md={{ span: 6 }}
            sm={{ span: 12 }}
            xs={{ span: 12 }}
            className="company-comparison-col"
          >
            <div
              role="button"
              tabIndex={0}
              className={classNames('card', { activeCard: key === activeIndex })}
              onClick={() => handleClick(key, company.companyId)}
              onKeyPress={() => handleClick(key, company.companyId)}
            >
              <CompanyCardLarge
                key={company?.companyId}
                companyData={company}
                showDrawerIcon={false}
                showSapIcon={false}
                isRecomended
              />
            </div>
          </Col>
        )) : null}
        {isLoading && (
          <div className="person-comparison-skeleton-col profile-confirmation">
            <CompanySkeleton />
          </div>
        )}
        {!isLoading && !companies?.length ? <NoDataWrapper><NoDataFound title="No Record Found" /></NoDataWrapper> : null}
      </Row>
    </InfiniteScroll>
  );

  return (
    <Modal
      open={isVisible}
      centered
      footer={null}
      width={910}
      wrapClassName="profile-comparison-popup"
      getContainer=""
      closable={false}
      maskStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(10px)' }}
    >
      <span className="popup-heading">
        {`Results
        (${count})`}
      </span>
      <div id="person-comparison-row" ref={scrollDivRef} className="w-100 h-100">
        {isPerson ? (
          getPersonInfiniteScroll()
        )
          : (
            getCompanyInfiniteScroll()
          )}
      </div>
    </Modal>
  );
};
SearchResultPopup.defaultProps = defaultProps;
export default SearchResultPopup;
